import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, fmtINR, STATUS_STYLE, API_BASE } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Check, X, Download, Truck, ArrowLeft, Clock, FileJson } from "lucide-react";
import { toast } from "sonner";

const STEPS = ["submitted","approved","so_raised","fulfilled"];
const STEP_LABEL = { draft: "Draft", submitted: "Submitted", approved: "Approved", so_raised: "SO Raised", fulfilled: "Fulfilled", rejected: "Rejected" };

export default function PoDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [po, setPo] = useState(null);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [soOpen, setSoOpen] = useState(false);
  const [adjust, setAdjust] = useState({});

  const load = () => api.get(`/po/${id}`).then(({ data }) => setPo(data));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [id]);

  if (!po) return <div className="text-slate-500">Loading...</div>;

  const status = STATUS_STYLE[po.status] || STATUS_STYLE.draft;
  const canApprove = ["accounts","manager"].includes(user?.role) && po.status === "submitted";
  const canRaiseSO = user?.role === "accounts" && po.status === "approved";
  const canFulfill = user?.role === "accounts" && po.status === "so_raised";
  const hasActions = canApprove || canRaiseSO || canFulfill;

  const approve = async () => {
    try { await api.post(`/po/${id}/approve`); toast.success("PO approved"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const reject = async () => {
    try { await api.post(`/po/${id}/reject`, { reason }); toast.success("PO rejected"); setRejectOpen(false); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const raiseSO = async () => {
    const line_adjustments = Object.entries(adjust).map(([product_id, confirmed_qty]) => ({ product_id, confirmed_qty: Number(confirmed_qty) }));
    try { await api.post(`/so`, { po_id: id, line_adjustments }); toast.success("Sales Order raised"); setSoOpen(false); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const fulfill = async () => {
    const so_id = po.so_id;
    if (!so_id) return;
    try { await api.post(`/so/${so_id}/fulfill`); toast.success("Marked fulfilled"); load(); }
    catch (e) { toast.error("Failed"); }
  };

  return (
    <div className="space-y-6" data-testid="po-detail-page">
      <button onClick={() => navigate("/po")} className="text-sm text-slate-600 hover:text-slate-900 inline-flex items-center gap-1" data-testid="back-to-pos">
        <ArrowLeft size={14} /> Back to purchase orders
      </button>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-medium tracking-tight" style={{ fontFamily: "'General Sans', system-ui, sans-serif" }}>{po.po_number}</h1>
            <Badge className={`${status.bg} ${status.text} font-medium`}>{status.label}</Badge>
          </div>
          <p className="text-sm text-slate-500 mt-1">Placed by <span className="font-medium text-slate-700">{po.dealer_name}</span> on {new Date(po.created_at).toLocaleString()}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-8 space-y-6">
          <Card className="p-6">
            <div className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500 mb-4">Line Items ({po.lines.length})</div>
            <table className="w-full text-sm">
              <thead className="text-xs text-slate-500 border-b border-slate-200">
                <tr>
                  <th className="text-left py-2">SKU</th>
                  <th className="text-left py-2">Item</th>
                  <th className="text-right py-2">Qty</th>
                  <th className="text-right py-2">Unit Price</th>
                  <th className="text-right py-2">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {po.lines.map(l => (
                  <tr key={l.product_id}>
                    <td className="py-3 font-mono text-xs">{l.sku}</td>
                    <td className="py-3">{l.name}</td>
                    <td className="py-3 text-right">{l.quantity}</td>
                    <td className="py-3 text-right">{fmtINR(l.unit_price)}</td>
                    <td className="py-3 text-right font-medium">{fmtINR(l.total || l.line_total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="mt-6 pt-4 border-t border-slate-200 flex justify-end">
              <div className="w-64 text-sm space-y-1.5">
                <div className="flex justify-between"><span className="text-slate-500">Subtotal</span><span>{fmtINR(po.subtotal || po.subtotal_amount)}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">GST 18%</span><span>{fmtINR(po.gst || po.tax_amount)}</span></div>
                <div className="flex justify-between text-lg font-medium pt-2 border-t border-slate-100"><span>Total</span><span>{fmtINR(po.total || po.total_amount)}</span></div>
              </div>
            </div>

            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-500">Billing</div>
                <div className="mt-1">{po.billing_address?.label}</div>
                <div className="text-slate-600 text-xs">{po.billing_address?.line1}, {po.billing_address?.city} - {po.billing_address?.pincode}</div>
                {po.billing_address?.gstin && <div className="text-xs text-slate-500 font-mono mt-1">GSTIN: {po.billing_address.gstin}</div>}
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-500">Shipping</div>
                <div className="mt-1">{po.shipping_address?.label}</div>
                <div className="text-slate-600 text-xs">{po.shipping_address?.line1}, {po.shipping_address?.city} - {po.shipping_address?.pincode}</div>
              </div>
            </div>
            {po.notes && <div className="mt-4 text-sm"><span className="text-slate-500 text-xs uppercase tracking-wider">Notes:</span> <span className="ml-1">{po.notes}</span></div>}
          </Card>
        </div>

        <div className="lg:col-span-4 space-y-6">
          <Card className="p-6">
            <div className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500 mb-4">Timeline</div>
            <ol className="space-y-3">
              {STEPS.map((s, idx) => {
                const currentIdx = STEPS.indexOf(po.status);
                const done = currentIdx >= idx && po.status !== "rejected";
                return (
                  <li key={s} className="flex items-start gap-3">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs mt-0.5 ${
                      done ? "bg-emerald-500 text-white" : "bg-slate-100 text-slate-400"
                    }`}>{done ? <Check size={12}/> : <Clock size={12}/>}</div>
                    <div>
                      <div className="text-sm font-medium">{STEP_LABEL[s]}</div>
                      {s === "submitted" && po.submitted_at && <div className="text-xs text-slate-500">{new Date(po.submitted_at).toLocaleString()}</div>}
                      {s === "approved" && po.approved_at && <div className="text-xs text-slate-500">{po.approved_by} · {new Date(po.approved_at).toLocaleString()}</div>}
                      {s === "so_raised" && po.so_number && <div className="text-xs text-slate-500 font-mono">{po.so_number}</div>}
                    </div>
                  </li>
                );
              })}
              {po.status === "rejected" && (
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full flex items-center justify-center text-xs mt-0.5 bg-rose-500 text-white"><X size={12}/></div>
                  <div><div className="text-sm font-medium">Rejected</div><div className="text-xs text-slate-500">{po.rejection_reason}</div></div>
                </li>
              )}
            </ol>
          </Card>

          {hasActions && (
            <Card className="p-6">
              <div className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500 mb-4">Actions</div>
              <div className="space-y-2">
                {canApprove && (
                  <>
                    <Button onClick={approve} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="approve-po-btn"><Check size={14} className="mr-2" />Approve PO</Button>
                    <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
                      <DialogTrigger asChild><Button variant="outline" className="w-full text-rose-700 border-rose-200 hover:bg-rose-50" data-testid="reject-po-btn"><X size={14} className="mr-2"/>Reject</Button></DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Reject PO {po.po_number}</DialogTitle>
                          <DialogDescription>Please state the reason for rejecting this purchase order.</DialogDescription>
                        </DialogHeader>
                        <Textarea placeholder="Reason for rejection..." value={reason} onChange={e=>setReason(e.target.value)} data-testid="reject-reason" />
                        <DialogFooter><Button onClick={reject} variant="destructive" data-testid="confirm-reject-btn">Reject</Button></DialogFooter>
                      </DialogContent>
                    </Dialog>
                  </>
                )}
                {canRaiseSO && (
                  <Dialog open={soOpen} onOpenChange={setSoOpen}>
                    <DialogTrigger asChild><Button className="w-full bg-[#0055FF] hover:bg-[#0044CC]" data-testid="raise-so-btn"><Truck size={14} className="mr-2"/>Raise Sales Order</Button></DialogTrigger>
                    <DialogContent className="max-w-lg">
                      <DialogHeader>
                        <DialogTitle>Raise SO from {po.po_number}</DialogTitle>
                        <DialogDescription>Confirm quantities. Leave blank to accept PO quantities. Stock will be decremented.</DialogDescription>
                      </DialogHeader>
                      <div className="space-y-2 max-h-64 overflow-y-auto">
                        {po.lines.map(l => (
                          <div key={l.product_id} className="flex items-center gap-3 text-sm">
                            <div className="flex-1"><div className="font-medium">{l.name}</div><div className="text-xs text-slate-500 font-mono">{l.sku}</div></div>
                            <div className="text-xs text-slate-500">PO: {l.quantity}</div>
                            <input type="number" min="0" max={l.quantity} defaultValue={l.quantity}
                                   onChange={e=>setAdjust({...adjust, [l.product_id]: e.target.value})}
                                   className="w-20 border border-slate-200 rounded-md px-2 py-1 text-sm" data-testid={`so-qty-${l.sku}`} />
                          </div>
                        ))}
                      </div>
                      <DialogFooter><Button onClick={raiseSO} className="bg-[#0055FF] hover:bg-[#0044CC]" data-testid="confirm-so-btn">Raise Sales Order</Button></DialogFooter>
                    </DialogContent>
                  </Dialog>
                )}
                {canFulfill && <Button onClick={fulfill} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="fulfill-btn"><Truck size={14} className="mr-2"/>Mark fulfilled</Button>}
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
